import { Component, Prop, Vue } from 'vue-property-decorator';
import TixLegend from '@/components/elements/TixLegend.vue';
import CalendarMonth from './CalendarMonth.vue';
import ScrollButton from './CalendarScrollButton.vue';
import { findClosestEnabledDate, scrollButtonHandler } from './helpers';
@Component({
    name: 'CalendarPicker',
    components: { TixLegend, CalendarMonth, ScrollButton },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    onSelect;
    @Prop({ required: true })
    index;
    @Prop({ required: true })
    months;
    @Prop({ required: true })
    venueTimezone;
    @Prop({ required: true })
    displayPrices;
    @Prop()
    capacityThreshold;
    // Internal UI state.
    focusedDate = null;
    visibleMonths = new Set();
    enabledButtons = {
        prev: false,
        next: true,
    };
    destroyed() {
        window.removeEventListener('keydown', this.handleKeydown);
    }
    mounted() {
        this.focusedDate = this.firstAvailableDate ?? null;
        window.addEventListener('keydown', this.handleKeydown);
        this.observeScrolling();
        this.scrollToMonth(this.focusedDate);
    }
    observeScrolling() {
        const handleScroll = (entries) => {
            for (const entry of entries) {
                const month = entry.target.getAttribute('data-id');
                if (entry.isIntersecting) {
                    this.visibleMonths.add(month);
                }
                else {
                    this.visibleMonths.delete(month);
                }
            }
            this.enabledButtons = {
                prev: !this.visibleMonths.has(this.firstMonth),
                next: !this.visibleMonths.has(this.lastMonth),
            };
        };
        // TODO Update DOM/TypeScript to a version that knows about delay and trackVisibility.
        const $wrapper = this.$refs.calendar;
        const observer = new IntersectionObserver(handleScroll, {
            root: $wrapper,
            rootMargin: '0px',
            threshold: 0.4,
            trackVisibility: true,
            delay: 300,
        });
        for (const monthId of this.monthIds) {
            observer.observe($wrapper.querySelector(`.picker-month[data-id="${monthId}"]`));
        }
    }
    handleScrollButtonPress(offset) {
        scrollButtonHandler(this.visibleMonths, offset);
    }
    handleKeydown(e) {
        const offset = this.arrowKeyOffset(e.code);
        if (offset) {
            e.preventDefault();
            const closest = findClosestEnabledDate(this.index, this.focusedDate, offset);
            if (closest) {
                this.focusedDate = closest;
            }
            // Scroll to the focussed date anyway, to bring it back into the viewport.
            this.scrollToMonth(this.focusedDate);
        }
        else if (e.code === 'Enter') {
            e.preventDefault();
            this.onSelect(this.index[this.focusedDate.date]);
        }
    }
    onFocus(day) {
        this.focusedDate = day.date;
    }
    arrowKeyOffset(keyCode) {
        const offsets = {
            ArrowUp: -7,
            ArrowRight: +1,
            ArrowDown: +7,
            ArrowLeft: -1,
        };
        return offsets[keyCode];
    }
    async scrollToMonth(date) {
        const calendarEl = this.$refs.calendar;
        const key = date.format('YYYY-MM');
        const month = calendarEl.querySelector(`.picker-month[data-id="${key}"]`);
        this.$nextTick(() => {
            month.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        });
    }
    get legendProps() {
        return {
            annotations: this.days.map((day) => day.annotations).flat(),
            areAnySoldOut: this.days.some((date) => date.status === 'sold_out'),
            arePricesDisplayed: this.displayPrices,
            hasPriceIncrease: false,
        };
    }
    get isSingleMonth() {
        return this.monthIds.length === 1;
    }
    get firstAvailableDate() {
        return this.days.find((day) => day.enabled)?.date;
    }
    get days() {
        return Object.values(this.index);
    }
    get monthIds() {
        return Object.keys(this.months);
    }
    get firstMonth() {
        return this.monthIds[0];
    }
    get lastMonth() {
        // Clone the array first; Vue does not protect against mutating the shared-result of computed properties.
        return [...this.monthIds].pop();
    }
}
